// Modules
import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { orderBy } from "lodash";

// Constants
import { Routes } from "constants/global";

// Services
import { bulkOfferIssuanceDraftList, bulkOfferIssuanceChangeStatus, bulkOfferIssuanceDownload } from "services/api/offerIssuanceApprove";

// Components
import { Button } from "antd";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// Redux
import { useAppSelector } from "app/hooks/hooks";
import { getLoggedInUserInfo } from "redux/userList/userSlice";

// Styles
import styles from "./OfferIssuanceApprove.module.css";

// Types
export type DraftOffer = {
	id: number;
	member_type: string;
	offer_id: string;
	cr_card_numbers: string;
	quantity: number;
	start_date: string | null;
	end_date: string | null;
	remarks: string;
	status: string;
	reject_reason: string | null;
	s3_key_path: string | null;
	s3_input_file?: string | null;
	s3_error_file?: string | null;
	s3_success_file?: string | null;
	created_at: string;
	created_by: string;
	updated_at: string;
	updated_by: string;
};

export type ApiResponse = {
	data: {
		result: DraftOffer[];
		total_count: number;
	};
	message: string;
	errors: any[];
};

// Default filter values
const DEFAULT_FILTERS = ["pending", "newest"];

// Utility functions
const showModal = (title: string, error = false) => {
	CountDownModal({
		timer: 2,
		title,
		errorIcon: error
	});
};

// Custom hooks
const useFilters = () => {
	const [selectedFilters, setSelectedFilters] = useState<string[]>(DEFAULT_FILTERS);
	const [showFilters, setShowFilters] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);

	const handleFilterChange = useCallback(({ value, checked, isTextInput }: { name: string; value: string; checked: boolean; isTextInput?: boolean }) => {
		if (isTextInput) return;

		setSelectedFilters((prev) => {
			if (["newest", "oldest"].includes(value)) {
				const withoutSortFilters = prev.filter((filter) => !["newest", "oldest"].includes(filter));
				return checked ? [...withoutSortFilters, value] : [...withoutSortFilters, "newest"];
			}

			if (["pending", "approved", "rejected", "processing", "completed", "failed"].includes(value)) {
				const currentStatusFilters = prev.filter((filter) => ["pending", "approved", "rejected", "processing", "completed", "failed"].includes(filter));
				if (!checked && currentStatusFilters.length === 1 && currentStatusFilters[0] === value) {
					return prev;
				}
				return checked ? [...prev, value] : prev.filter((filter) => filter !== value);
			}

			return prev;
		});
		setCurrentPage(1);
	}, []);

	const handleFilterReset = useCallback(() => {
		setSelectedFilters(DEFAULT_FILTERS);
		setCurrentPage(1);
	}, []);

	const toggleFilters = useCallback(() => {
		setShowFilters((prev) => !prev);
	}, []);

	return {
		selectedFilters,
		showFilters,
		currentPage,
		handleFilterChange,
		handleFilterReset,
		toggleFilters,
		setCurrentPage
	};
};

const useModalState = () => {
	const [modalVisible, setModalVisible] = useState(false);
	const [modalAction, setModalAction] = useState<"approve" | "reject" | "cancel" | null>(null);
	const [selectedRecord, setSelectedRecord] = useState<DraftOffer | null>(null);
	const [modalLoading, setModalLoading] = useState(false);
	const [rejectReason, setRejectReason] = useState("");
	const [rejectReasonError, setRejectReasonError] = useState(false);

	const resetModal = useCallback(() => {
		setModalVisible(false);
		setModalAction(null);
		setSelectedRecord(null);
		setRejectReason("");
		setRejectReasonError(false);
	}, []);

	const handleRejectReasonChange = useCallback((value: string) => {
		setRejectReason(value);
		setRejectReasonError(false);
	}, []);

	return {
		modalVisible,
		modalAction,
		selectedRecord,
		modalLoading,
		rejectReason,
		rejectReasonError,
		setModalVisible,
		setModalAction,
		setSelectedRecord,
		setModalLoading,
		handleRejectReasonChange,
		setRejectReasonError,
		resetModal
	};
};

const useDraftList = (currentPage: number, pageSize: number, selectedFilters: string[]) => {
	const [loading, setLoading] = useState(false);
	const [draftList, setDraftList] = useState<ApiResponse | null>(null);

	const fetchDraftList = useCallback(async () => {
		setLoading(true);
		try {
			const selectedStatusFilters = selectedFilters
				.filter((filter) => ["pending", "approved", "rejected", "processing", "completed", "failed"].includes(filter))
				.map((status) => {
					const statusMap: Record<string, string> = {
						pending: "PENDING_APPROVAL",
						approved: "APPROVED",
						rejected: "REJECTED",
						processing: "PROCESSING",
						completed: "COMPLETED",
						failed: "FAILED"
					};
					return statusMap[status] || status.toUpperCase();
				});

			const response = await bulkOfferIssuanceDraftList({
				status: selectedStatusFilters,
				page_number: currentPage.toString(),
				page_records: pageSize.toString()
			});

			if (response.data.errors?.length > 0) {
				showModal(response.data.errors[0].message, true);
				return;
			}

			setDraftList(response.data);
		} catch (error: any) {
			showModal(error.response?.data?.errors[0]?.message || "Something went wrong", true);
		} finally {
			setLoading(false);
		}
	}, [currentPage, pageSize, selectedFilters]);

	useEffect(() => {
		fetchDraftList();
	}, [fetchDraftList]);

	const sortedDraftList = useMemo(() => {
		if (!draftList?.data?.result) return [];

		const sortFilter = selectedFilters.find((filter) => ["newest", "oldest"].includes(filter)) || "newest";
		const data = [...draftList.data.result];

		return orderBy(data, [(item) => new Date(item.created_at).getTime()], [sortFilter === "newest" ? "desc" : "asc"]);
	}, [draftList, selectedFilters]);

	return {
		loading,
		draftList,
		sortedDraftList,
		fetchDraftList
	};
};

const useStatusActions = (selectedRecord: DraftOffer | null, fetchDraftList: () => Promise<void>, resetModal: () => void) => {
	const handleStatusChange = useCallback(
		async (nextStatus: "APPROVED" | "REJECTED" | "CANCELLED", rejectReasonText?: string) => {
			if (!selectedRecord) return { error: true };

			if (nextStatus === "REJECTED" && !rejectReasonText?.trim()) {
				return { error: true };
			}

			try {
				const response = await bulkOfferIssuanceChangeStatus({
					draft_id: selectedRecord.id,
					next_status: nextStatus,
					...(rejectReasonText && { reject_reason: rejectReasonText.trim() })
				});

				if (response.data.errors?.length > 0) {
					showModal(response.data.errors[0].message, true);
					return { error: true };
				}

				showModal(`Offer ${nextStatus.toLowerCase()} successfully`);
				resetModal();
				await fetchDraftList();
				return { error: false };
			} catch (error: any) {
				showModal(error.response?.data?.errors[0]?.message || "Something went wrong", true);
				return { error: true };
			}
		},
		[selectedRecord, fetchDraftList, resetModal]
	);

	return { handleStatusChange };
};

const useDownloadS3Files = () => {
	const [isDownloading, setIsDownloading] = useState<Record<string, boolean>>({});

	const handleDownload = useCallback(async (record: DraftOffer, fileType: "input" | "success" | "error") => {
		const loadingKey = `${record.id}_${fileType}`;
		try {
			setIsDownloading((prev) => ({ ...prev, [loadingKey]: true }));
			const response = await bulkOfferIssuanceDownload({
				draft_id: record.id,
				file_type: fileType
			});

			if (response.data?.data?.file_url) {
				window.location.href = response.data.data.file_url;
			}
		} catch (error: any) {
			showModal(error.response?.data?.errors?.[0]?.message || `Failed to download ${fileType} file`, true);
		} finally {
			setIsDownloading((prev) => ({ ...prev, [loadingKey]: false }));
		}
	}, []);

	return { handleDownload, isDownloading };
};

const useActionButtons = (
	handleAction: (record: DraftOffer, action: "approve" | "reject" | "cancel") => void,
	isCreatedByCurrentUser: (record: DraftOffer) => boolean,
	handleDownload: (record: DraftOffer, fileType: "input" | "success" | "error") => Promise<void>,
	isDownloading: Record<string, boolean>
) => {
	const userDetails = useAppSelector(getLoggedInUserInfo);
	const hasApprovePermission =
		userDetails?.data?.[0]?.groups?.some((group: any) => group.permissions?.some((permission: any) => permission.perm_code === "OFFER_ISSUANCE_APPROVE")) || false;

	return useCallback(
		(record: DraftOffer) => {
			if (record.status === "pending_approval") {
				if (isCreatedByCurrentUser(record)) {
					return (
						<div className="flex flex-col gap-2">
							<Button danger className={styles.reject_button} onClick={() => handleAction(record, "cancel")}>
								Cancel
							</Button>
						</div>
					);
				} else if (hasApprovePermission) {
					return (
						<div className="flex flex-col gap-2">
							<Button type="primary" className={styles.approve_button} onClick={() => handleAction(record, "approve")}>
								Approve
							</Button>
							<Button danger className={styles.reject_button} onClick={() => handleAction(record, "reject")}>
								Reject
							</Button>
						</div>
					);
				}
			}

			// Conditional download buttons based on S3 file availability
			const buttons = [];

			if (record.s3_input_file && record.s3_input_file.trim() !== "") {
				buttons.push(
					<Button
						key="download-input"
						className={styles.download_button}
						type="default"
						onClick={() => handleDownload(record, "input")}
						loading={isDownloading[`${record.id}_input`]}
					>
						Download Input File
					</Button>
				);
			}

			if (record.s3_error_file && record.s3_error_file.trim() !== "") {
				buttons.push(
					<Button
						key="download-error"
						className={styles.download_error_button}
						danger
						onClick={() => handleDownload(record, "error")}
						loading={isDownloading[`${record.id}_error`]}
					>
						Download Error File
					</Button>
				);
			}

			if (record.s3_success_file && record.s3_success_file.trim() !== "") {
				buttons.push(
					<Button
						key="download-success"
						className={styles.download_success_button}
						type="default"
						onClick={() => handleDownload(record, "success")}
						loading={isDownloading[`${record.id}_success`]}
					>
						Download Success File
					</Button>
				);
			}

			if (buttons.length > 0) {
				return <div className="flex flex-col gap-2">{buttons}</div>;
			}

			return null;
		},
		[handleAction, isCreatedByCurrentUser, handleDownload, isDownloading, hasApprovePermission]
	);
};

export const useOfferIssuanceApprove = () => {
	const navigate = useNavigate();
	const userDetails = useAppSelector(getLoggedInUserInfo);
	const [pageSize] = useState(10);

	// Add permission check
	const hasCreatePermission =
		userDetails?.data?.[0]?.groups?.some((group: any) => group.permissions?.some((permission: any) => permission.perm_code === "OFFER_ISSUANCE_CREATE")) || false;

	const { selectedFilters, showFilters, currentPage, handleFilterChange, handleFilterReset, toggleFilters, setCurrentPage } = useFilters();

	const {
		modalVisible,
		modalAction,
		selectedRecord,
		modalLoading,
		rejectReason,
		rejectReasonError,
		setModalVisible,
		setModalAction,
		setSelectedRecord,
		setModalLoading,
		handleRejectReasonChange,
		setRejectReasonError,
		resetModal
	} = useModalState();

	const { loading, draftList, sortedDraftList, fetchDraftList } = useDraftList(currentPage, pageSize, selectedFilters);

	const isCreatedByCurrentUser = useCallback((record: DraftOffer) => record.created_by === userDetails?.data?.[0]?.username, [userDetails]);

	const { handleStatusChange } = useStatusActions(selectedRecord, fetchDraftList, resetModal);

	const { handleDownload, isDownloading } = useDownloadS3Files();

	const handleCreateOffer = useCallback(() => {
		navigate(Routes.OFFER_ISSUANCE);
	}, [navigate]);

	const handleAction = useCallback((record: DraftOffer, action: "approve" | "reject" | "cancel") => {
		setSelectedRecord(record);
		setModalAction(action);
		setModalVisible(true);
	}, []);

	const handleModalOk = useCallback(async () => {
		if (!modalAction) return;

		setModalLoading(true);
		const statusMap = {
			approve: "APPROVED",
			reject: "REJECTED",
			cancel: "CANCELLED"
		} as const;

		const result = await handleStatusChange(statusMap[modalAction], modalAction === "reject" ? rejectReason : undefined);

		if (result?.error && modalAction === "reject") {
			setRejectReasonError(true);
		}
		setModalLoading(false);
	}, [modalAction, rejectReason, handleStatusChange]);

	const getActionButtons = useActionButtons(handleAction, isCreatedByCurrentUser, handleDownload, isDownloading);

	return {
		loading,
		draftList: draftList ? { ...draftList, data: { ...draftList.data, result: sortedDraftList } } : null,
		handleCreateOffer,
		handleFilterChange,
		selectedFilters,
		handleFilterReset,
		handlePagination: setCurrentPage,
		currentPage,
		pageSize,
		getActionButtons,
		showFilters,
		toggleFilters,
		showModal,
		isCreatedByCurrentUser,
		modalVisible,
		modalAction,
		modalLoading,
		handleModalOk,
		handleModalCancel: resetModal,
		rejectReason,
		rejectReasonError,
		handleRejectReasonChange,
		selectedRecord,
		handleDownload,
		isDownloading,
		hasCreatePermission
	};
};
